import { Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { api, ApiError, Appointment, AppointmentStatus } from "../lib/api";

interface ClientAppointmentsProps {
  clientId: string;
}

function formatDateTime(isoString: string): string {
  const date = new Date(isoString);
  return date.toLocaleDateString("en-US", {
    weekday: "short",
    month: "short",
    day: "numeric",
    hour: "numeric",
    minute: "2-digit",
  });
}

function formatDuration(startTime: string, endTime: string): string {
  const start = new Date(startTime);
  const end = new Date(endTime);
  const minutes = Math.round((end.getTime() - start.getTime()) / 60000);
  if (minutes >= 60) {
    const hours = Math.floor(minutes / 60);
    const mins = minutes % 60;
    return mins > 0 ? `${hours}h ${mins}m` : `${hours}h`;
  }
  return `${minutes} min`;
}

const statusConfig: Record<AppointmentStatus, { label: string; className: string }> = {
  scheduled: { label: "Scheduled", className: "bg-blue-100 text-blue-700" },
  confirmed: { label: "Confirmed", className: "bg-green-100 text-green-700" },
  in_progress: { label: "In Progress", className: "bg-yellow-100 text-yellow-700" },
  completed: { label: "Completed", className: "bg-gray-100 text-gray-700" },
  cancelled: { label: "Cancelled", className: "bg-red-100 text-red-700" },
  no_show: { label: "No Show", className: "bg-orange-100 text-orange-700" },
};

function StatusBadge({ status }: { status: AppointmentStatus }) {
  const config = statusConfig[status] || { label: status, className: "bg-gray-100 text-gray-600" };
  return (
    <span className={`inline-flex px-2 py-0.5 text-xs font-medium rounded-full ${config.className}`}>
      {config.label}
    </span>
  );
}

function AppointmentItem({ appointment }: { appointment: Appointment }) {
  return (
    <Link
      to={`/appointments/${appointment.id}`}
      className="block p-4 bg-white border border-gray-200 rounded-lg hover:border-amber-300 hover:shadow-sm transition-all"
    >
      <div className="flex items-start justify-between">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-1">
            <span className="font-medium text-gray-900">
              {formatDateTime(appointment.startTime)}
            </span>
            <StatusBadge status={appointment.status} />
          </div>
          <div className="text-sm text-gray-600">
            {appointment.serviceName || "Service"}
          </div>
        </div>
        <div className="text-sm text-gray-500 ml-4">
          {formatDuration(appointment.startTime, appointment.endTime)}
        </div>
      </div>
    </Link>
  );
}

export function ClientAppointments({ clientId }: ClientAppointmentsProps) {
  const { data, isLoading, error } = useQuery({
    queryKey: ["appointments", "byClient", clientId],
    queryFn: () => api.appointments.list({ clientId, limit: 100 }),
    enabled: !!clientId,
  });

  const appointments = data?.data || [];
  const now = new Date();

  // Split into upcoming and past
  const upcoming = appointments
    .filter((a) => new Date(a.startTime) >= now && !["cancelled", "no_show"].includes(a.status))
    .sort((a, b) => new Date(a.startTime).getTime() - new Date(b.startTime).getTime());

  const past = appointments
    .filter((a) => new Date(a.startTime) < now || ["cancelled", "no_show", "completed"].includes(a.status))
    .filter((a) => !upcoming.includes(a))
    .sort((a, b) => new Date(b.startTime).getTime() - new Date(a.startTime).getTime());

  if (isLoading) {
    return (
      <div className="mt-8">
        <h2 className="text-xl font-semibold text-gray-900 mb-4">Appointments</h2>
        <div className="text-center py-8">
          <div className="animate-pulse text-gray-500">Loading appointments...</div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="mt-8">
        <h2 className="text-xl font-semibold text-gray-900 mb-4">Appointments</h2>
        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
          <p className="text-red-700">
            {error instanceof ApiError ? error.message : "Failed to load appointments"}
          </p>
        </div>
      </div>
    );
  }

  if (appointments.length === 0) {
    return (
      <div className="mt-8">
        <h2 className="text-xl font-semibold text-gray-900 mb-4">Appointments</h2>
        <div className="bg-gray-50 rounded-lg p-8 text-center">
          <p className="text-gray-500">No appointments for this client.</p>
          <Link
            to={`/appointments/new?clientId=${clientId}`}
            className="mt-4 inline-block text-amber-600 hover:text-amber-700 font-medium"
          >
            Book an appointment
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="mt-8">
      <h2 className="text-xl font-semibold text-gray-900 mb-4">
        Appointments ({appointments.length})
      </h2>

      {upcoming.length > 0 && (
        <div className="mb-6">
          <h3 className="text-sm font-medium text-gray-500 uppercase tracking-wider mb-3">
            Upcoming ({upcoming.length})
          </h3>
          <div className="space-y-2">
            {upcoming.map((appointment) => (
              <AppointmentItem key={appointment.id} appointment={appointment} />
            ))}
          </div>
        </div>
      )}

      {past.length > 0 && (
        <div>
          <h3 className="text-sm font-medium text-gray-500 uppercase tracking-wider mb-3">
            Past ({past.length})
          </h3>
          <div className="space-y-2">
            {past.map((appointment) => (
              <AppointmentItem key={appointment.id} appointment={appointment} />
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
